/**
 * FFT bin to fractional-octave band aggregation.
 *
 * Given an FFT with N bins at a given sample rate, this module maps
 * each bin into 1/N-octave bands, sums energy (linear power), and
 * converts to dB.
 */

import type { OctaveBand } from "../types";

/**
 * Generate 1/N-octave band center frequencies from minFreq to maxFreq.
 * Uses base-2 octave fractions: f_k = f_ref * 2^(k/N)
 * where f_ref = 1000 Hz and k is chosen to span [minFreq, maxFreq].
 */
export function generateBandCenters(
  fraction: number,
  minFreq: number = 20,
  maxFreq: number = 20000
): OctaveBand[] {
  const fRef = 1000;
  const bands: OctaveBand[] = [];

  // Find the range of k values
  const kMin = Math.ceil(fraction * Math.log2(minFreq / fRef));
  const kMax = Math.floor(fraction * Math.log2(maxFreq / fRef));

  for (let k = kMin; k <= kMax; k++) {
    const center = fRef * Math.pow(2, k / fraction);
    const halfBand = Math.pow(2, 1 / (2 * fraction));
    const low = center / halfBand;
    const high = center * halfBand;

    if (center >= minFreq && center <= maxFreq) {
      bands.push({
        center,
        low,
        high,
        index: bands.length,
      });
    }
  }

  return bands;
}

/**
 * Build a mapping from band index to the FFT bin indices that fall within it.
 */
export function buildBinMap(
  bands: OctaveBand[],
  fftSize: number,
  sampleRate: number
): Int32Array[] {
  const binCount = fftSize / 2;
  const binWidth = sampleRate / fftSize;
  const map: Int32Array[] = [];

  for (const band of bands) {
    const lowBin = Math.max(1, Math.floor(band.low / binWidth));
    const highBin = Math.min(binCount - 1, Math.ceil(band.high / binWidth));
    const bins: number[] = [];
    for (let b = lowBin; b <= highBin; b++) {
      bins.push(b);
    }
    map.push(new Int32Array(bins));
  }

  return map;
}

/**
 * Aggregate FFT magnitude data (in dB, from AnalyserNode.getFloatFrequencyData)
 * into octave bands by energy-summing.
 *
 * @param fftData - Float32Array of dB values from getFloatFrequencyData
 * @param binMap - mapping from band index to FFT bin indices
 * @returns Float64Array of dB values per band
 */
export function aggregateToBands(
  fftData: Float32Array,
  binMap: Int32Array[]
): Float64Array {
  const result = new Float64Array(binMap.length);

  for (let i = 0; i < binMap.length; i++) {
    const bins = binMap[i];
    if (bins.length === 0) {
      result[i] = -Infinity;
      continue;
    }

    let powerSum = 0;
    for (let j = 0; j < bins.length; j++) {
      const dbVal = fftData[bins[j]];
      // Convert dB to linear power, sum
      powerSum += Math.pow(10, dbVal / 10);
    }

    // Sum power across all bins in the band (not average).
    // For proportional-bandwidth analysis, summing gives the total
    // energy in each band. Pink noise should read flat since wider
    // bands contain proportionally more bins.
    result[i] = 10 * Math.log10(powerSum);
  }

  return result;
}

/**
 * Accumulate energy for time-averaging.
 * Call this each frame; then call finalizeAverage when done.
 */
export function accumulateEnergy(
  accumulator: Float64Array,
  bandData: Float64Array,
  frameCount: { value: number }
): void {
  for (let i = 0; i < bandData.length; i++) {
    // Accumulate in linear power domain
    accumulator[i] += Math.pow(10, bandData[i] / 10);
  }
  frameCount.value++;
}

/**
 * Finalize the time-average: divide accumulated power by frame count,
 * convert to dB.
 */
export function finalizeAverage(
  accumulator: Float64Array,
  frameCount: number
): Float64Array {
  const result = new Float64Array(accumulator.length);
  for (let i = 0; i < accumulator.length; i++) {
    result[i] = 10 * Math.log10(accumulator[i] / frameCount);
  }
  return result;
}
